import { cn } from "@/lib/utils";
import { FEATURES } from "@/config";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowUp, FileText, LoaderCircle, Mic, Paperclip } from "lucide-react";

interface ChatInputActionBarProps {
  // Document panel toggle (mobile)
  onOpenDocument?: () => void;

  // Audio recording
  isRecording: boolean;
  onStartRecording?: () => void;
  onStopRecording?: () => void;

  // File upload
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputId: string;

  // Submit/Stop
  isLoading: boolean;
  canSubmit: boolean;
  onStop: () => void;
  isAgentBusy: boolean;

  // Customization
  submitIcon?: "arrow-right" | "arrow-up";
}

/**
 * Vertical action bar for ChatInput with all interaction buttons.
 *
 * Contains:
 * - Document button (mobile only)
 * - Audio recording toggle
 * - File upload button
 * - Submit/Stop button
 */
export function ChatInputActionBar({
  onOpenDocument,
  isRecording,
  onStartRecording,
  onStopRecording,
  handleFileUpload,
  fileInputId,
  isLoading,
  canSubmit,
  onStop,
  isAgentBusy,
  submitIcon = "arrow-right",
}: ChatInputActionBarProps) {
  return (
    <div className="flex flex-col gap-1 p-2 border-l border-utrecht-blue/20">
      {/* Document button - mobile only */}
      {onOpenDocument && (
        <Button
          type="button"
          size="icon"
          onClick={onOpenDocument}
          className="rounded-none bg-primary hover:bg-primary/90 text-white"
          aria-label="Open document"
        >
          <FileText className="size-5" />
        </Button>
      )}

      {/* Audio recording button - toggles between start/stop */}
      {FEATURES.audioRecordingEnabled && onStartRecording && onStopRecording && (
        <Button
          type="button"
          size="icon"
          onClick={() => {
            if (isRecording) {
              onStopRecording();
            } else {
              onStartRecording();
            }
          }}
          className={cn(
            "rounded-none text-white",
            isRecording
              ? "bg-utrecht-red/50 hover:bg-utrecht-red/80 animate-pulse"
              : "bg-utrecht-red hover:bg-utrecht-red"
          )}
          aria-label={isRecording ? "Stop recording" : "Start recording"}
        >
          <Mic className="size-5" />
        </Button>
      )}

      {/* File upload input */}
      {FEATURES.fileUploadEnabled && (
        <>
          <input
            id={fileInputId}
            type="file"
            onChange={handleFileUpload}
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,audio/webm,video/webm,audio/mp4,audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/wave,audio/x-wav,audio/aac,audio/x-m4a,audio/m4a,audio/flac"
            className="hidden"
          />
          <Button
            type="button"
            size="icon"
            className="rounded-none bg-utrecht-gold hover:bg-utrecht-gold text-white"
            asChild
          >
            <Label htmlFor={fileInputId}>
              <Paperclip className="size-5" />
            </Label>
          </Button>
        </>
      )}

      {/* Submit/Stop button */}
      {isLoading ? (
        <Button
          key="stop"
          size="icon"
          onClick={onStop}
          className="rounded-none shadow-md transition-all"
          data-testid="interrupt-button"
        >
          <LoaderCircle className="h-4 w-4 animate-spin" />
        </Button>
      ) : (
        <Button
          type="submit"
          size="icon"
          className="rounded-none shadow-md transition-all"
          disabled={!canSubmit || isAgentBusy}
          data-testid="send-button"
        >
          {submitIcon === "arrow-up" ? (
            <ArrowUp className="h-4 w-4" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
}
